import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '__generated__/globalTypes';
import { getCardRepresentation } from 'lib/cards';
import { isType } from 'lib/gql';
import { lazy } from 'lib/retry';

import { DeliverableReward } from '../DeliverableReward';
import { InGameCurrency3d } from '../InGameCurrency';
import type { ProbabilisticBundleReward_probabilisticBundleSlotItemInterface } from './__generated__/index.graphql';

const Card = lazy(
  async () => import('@sorare/trois/src/atoms/Card/Card'),
  'Card'
);

const Essence = lazy(
  async () => import('@sorare/trois/src/atoms/Essence'),
  'Essence'
);

const ConversionCredit = lazy(
  async () => import('@sorare/trois/src/atoms/ConversionCredit'),
  'ConversionCredit'
);

type Props = {
  item: ProbabilisticBundleReward_probabilisticBundleSlotItemInterface;
};

export const ProbabilisticBundleReward = ({ item }: Props) => {
  if (isType(item, 'ProbabilisticBundleSlotCardItem')) {
    if (item.anyCard) {
      return <Card card={getCardRepresentation(item.anyCard)} />;
    }
    return null;
  }
  if (isType(item, 'ProbabilisticBundleSlotInGameCurrencyItem')) {
    return <InGameCurrency3d item={item} />;
  }

  if (isType(item, 'ProbabilisticBundleSlotCardShardItem')) {
    return (
      <Essence
        rarity={
          (
            {
              [Rarity.common]: 'common',
              [Rarity.custom_series]: 'limited',
              [Rarity.limited]: 'limited',
              [Rarity.rare]: 'rare',
              [Rarity.super_rare]: 'super_rare',
              [Rarity.unique]: 'unique',
            } as const
          )[item.rarity]
        }
        scale={0.5}
      />
    );
  }

  if (isType(item, 'ProbabilisticBundleSlotConversionCreditItem')) {
    return <ConversionCredit scale={0.25} />;
  }

  if (isType(item, 'ProbabilisticBundleSlotDeliverableItem')) {
    return item.rewardDeliverableItem ? (
      <DeliverableReward
        deliverableItem={item.rewardDeliverableItem.deliverableItem}
      />
    ) : null;
  }

  // eslint-disable-next-line no-console
  console.error('Unknown item type', item);
  return null;
};

ProbabilisticBundleReward.fragments = {
  probabilisticBundleSlotItemInterface: gql`
    fragment ProbabilisticBundleReward_probabilisticBundleSlotItemInterface on ProbabilisticBundleSlotItemInterface {
      id

      ... on ProbabilisticBundleSlotCardItem {
        id
        anyCard {
          slug
          ...getCardRepresentation_anyCardInterface
        }
      }
      ... on ProbabilisticBundleSlotCardShardItem {
        id
        rarity
      }
      ... on ProbabilisticBundleSlotConversionCreditItem {
        id
      }
      ... on ProbabilisticBundleSlotDeliverableItem {
        id
        rewardDeliverableItem {
          deliverableItem {
            slug
            ...DeliverableReward_deliverableItemInterface
          }
        }
      }
      ... on ProbabilisticBundleSlotInGameCurrencyItem {
        id
        ...InGameCurrency3d_ProbabilisticBundleSlotInGameCurrencyItem
      }
    }
    ${getCardRepresentation.fragments.anyCardInterface}
    ${DeliverableReward.fragments.deliverableItemInterface}
    ${InGameCurrency3d.fragments.ProbabilisticBundleSlotInGameCurrencyItem}
  ` as TypedDocumentNode<ProbabilisticBundleReward_probabilisticBundleSlotItemInterface>,
};
